<template>
  <div>
    <Head title="Přidat komponentu" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/components">Sety</Link>
      <span class="text-indigo-400 font-medium">/</span> Vytvořit
    </h1>
    <div class="bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-model="form.name" :error="form.errors.name" class="pb-8 pr-6 w-full lg:w-1/2" label="Název" />
          <text-input v-model="form.model" :error="form.errors.model" class="pb-8 pr-6 w-full lg:w-1/2" label="Model" />
          <text-input v-model="form.panel_power" :error="form.errors.panel_power" type="number" min="0" step="any" class="pb-8 pr-6 w-full lg:w-1/2" label="Solární panely (kWp)" />
          <text-input v-model="form.output_power" :error="form.errors.output_power" type="number" min="0" step="any" class="pb-8 pr-6 w-full lg:w-1/2" label="Výstupní výkon (KW)" />
          <text-input v-model="form.output_voltage" :error="form.errors.output_voltage" class="pb-8 pr-6 w-full lg:w-1/2" label="Výstupní napětí" />
          <SelectInput v-model="form.subsidy_type" :error="form.errors.subsidy_type" class="pb-8 pr-6 w-full lg:w-1/2" label="Typ dotace">
            <option value="nzu" selected="selected">NZÚ</option>
            <option value="nzu-light">NZÚ light</option>
            <option value="babicka">Babička</option>
            <option value="without-subsidy">Bez dotace</option>
          </SelectInput>
          <TextAreaInput v-model="form.note" :error="form.errors.note" class="pb-8 pr-6 w-full" label="Poznámka" />
          <div class="mt-4 w-full">
            <h2 class="text-2xl font-bold">Komponenty</h2>
          </div>
          <div v-if="componentList.length !== 0" class="flex items-start justify-items-stretch my-4 w-full">
            <SelectInput ref="newComponent" v-model="newComponent" :error="form.errors.components" class="">
              <option :value="null" disabled selected>Vyberte komponentu</option>
              <option v-for="component in componentList" :key="component.id" :value="component">{{ component.name }}</option>
            </SelectInput>
            <button class="btn-indigo ml-3 mt-auto" @click.prevent="addComponent()">
              <span>Přidat komponentu</span>
            </button>
          </div>
          <div class="mb-4 w-full">
            <table v-if="form.components.length !== 0" class="w-full whitespace-nowrap">
              <thead>
                <tr class="text-left font-bold">
                  <th class="pb-4 pt-6 text-xs">Komponenta</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Výchozí počet</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Min</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Max</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Editace</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Alespoň 1</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Barva</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Přepsat cenu</th>
                  <th class="pb-4 pt-6 px-2 text-xs">Cena</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="component in form.components" :key="component.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
                  <td class="border-t">
                    <label class="flex items-center mt-2 py-3" :for="'component-' + component.id">
                      <icon name="trash" class="flex-shrink-0 ml-2 w-4 h-4 fill-gray-400 cursor-pointer" @click="removeComponent(component)" />
                      <span class="ml-2 text-xs">{{ component.name }}</span>
                    </label>
                  </td>
                  <td class="px-2 border-t"><text-input v-model="component.quantity" type="number" min="0" required step="any" class="w-full" /></td>
                  <td class="px-2 border-t">
                    <text-input v-model="component.min" type="number" min="0" required step="any" class="w-full" />
                  </td>
                  <td class="px-2 border-t">
                    <text-input v-model="component.max" type="number" min="0" required step="any" class="w-full" />
                  </td>
                  <td class="px-2 text-center border-t">
                    <input v-model="component.editable" class="mr-2" type="checkbox" />
                  </td>
                  <td class="px-2 text-center border-t">
                    <input v-model="component.group_required" class="mr-2" type="checkbox" />
                  </td>
                  <td class="px-2 text-center border-t">
                    <input v-model="component.color" class="border-1 mr-2 w-6 h-6 border-gray-300 cursor-pointer" type="color" />
                  </td>
                  <td class="px-2 text-center border-t">
                    <input v-model="component.is_override_price" class="mr-2" type="checkbox" />
                  </td>
                  <td class="px-2 text-center border-t" v-if="component.is_override_price">
                    <text-input v-model="component.override_price" type="number" min="0" required step="any" class="w-full" />
                  </td>
                  <td class="px-2 text-center text-xs border-t" v-else>
                    {{ $filters.currency(component.unit_price) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo" type="submit">Vytvořit set</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link, Router } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextAreaInput from '@/Shared/TextAreaInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Icon from '@/Shared/Icon'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    Icon,
    TextAreaInput,
  },
  layout: Layout,
  props: {
    components: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        name: null,
        model: null,
        panel_power: null,
        output_power: null,
        output_voltage: null,
        subsidy_type: 'nzu',
        note: null,
        components: [],
      }),
      componentList: this.components,
      newComponent: null,
    }
  },

  //Odeber z componentList komponentu, která byla přidána do form.components
  watch: {
    form: {
      handler() {
        this.resetComponentList()
      },
      deep: true,
    },
  },

  methods: {
    store() {
      this.form.post('/sets')
    },
    addComponent() {
      if (!this.newComponent) {
        return
      }
      if (this.form.components.find((component) => component.id === this.newComponent.id)) {
        return
      }
      this.form.components.push(this.newComponent)
    },
    removeComponent(component) {
      this.form.components = this.form.components.filter((c) => c.id !== component.id)
    },
    resetComponentList() {
      this.componentList = this.components.filter((component) => {
        return !this.form.components.find((formComponent) => formComponent.id === component.id)
      })
    },
  },
}
</script>
